import React, {useState, useEffect, useRef} from 'react';
import classnames from 'classnames';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import {useDrop} from 'react-dnd';
import {NativeTypes} from 'react-dnd-html5-backend';
import {DndProvider} from 'react-dnd'
import {HTML5Backend} from 'react-dnd-html5-backend';
import ButtonCommon from '../../../../../../common/components/ButtonCommon';
import EmptyView from '../../../../../../common/components/Empty';
import {getFileTypeIcon} from '../../../../../../common/utils/files/getFileTypeIcon';
import fileIcon from '../../../../../../common/assets/file_present-24px.svg';
import remove from '../../../../../../common/assets/delete-24px.svg';
import styles from './styles.module.scss';

const ItemPasteFile = ({
                         label,
                         ...rest
                       }) => {
  const [showModal, setShowModal] = useState(false);
  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  return (
    <>
      <div
        className={styles.optionOpenModal}
        onClick={handleShowModal}
      >
        <img src={fileIcon} alt="file"/>
        <span className={styles.optionLabel}>{label}</span>
      </div>
      <Modal
        isOpen={showModal}
        onRequestClose={handleCloseModal}
        className={styles.modal}
        overlayClassName={styles.overlay}
        shouldCloseOnOverlayClick={false}
      >
        <DndProvider backend={HTML5Backend}>
          <UploadFile
            handleCloseModal={handleCloseModal}
            {...rest}
          />
        </DndProvider>
      </Modal>
    </>
  )
};


const UploadFile = ({
                      fileTypeID,
                      fileActions,
                      multiple = false,
                      handleCloseModal,
                      ...rest
                    }) => {
  const [files, setFiles] = useState([]);
  // drag-drop
  const [{canDrop, isOver}, drop] = useDrop(
    () => ({
      accept: [NativeTypes.FILE],
      drop(item) {
        if (item) {
          if (multiple) {
            setFiles(item.files);
          } else {
            setFiles([item.files[0]])
          }
        }
      },
      canDrop(item) {
        return true
      },
      collect: (monitor) => {
        return {
          isOver: monitor.isOver(),
          canDrop: monitor.canDrop(),
        }
      },
    }),
    [setFiles],
  );
  // copy-paste
  useEffect(() => {
    const paste = (evt) => {
      const clipboardItems = evt.clipboardData.items;
      const items = [].slice.call(clipboardItems)
      if (items.length === 0) {
        return;
      }
      if (multiple) {
        const array = items.map(item => {
          return item.getAsFile();
        })
        setFiles(array)
      } else {
        const blob = items[0].getAsFile();
        setFiles([blob])
      }

    };
    document.addEventListener('paste', paste);
    return () => {
      document.removeEventListener('paste', paste);
    };
  }, []);
  const onRemoveFile = (fileName) => {
    const newFilesArray = files.filter(item => item.name !== fileName);
    setFiles(newFilesArray);
  };
  const selectedFile = useRef(null);
  const handleChange = () => {
    const selected = multiple ? files : files[0];
    if (multiple) {
      fileActions.handleUploadMultipleFiles(selected, fileTypeID, null)
    } else {
      fileActions.handleUploadFile(selected, fileTypeID)
    }
  };

  const EditableFileName = ({file, index}) => {
    const cleanName = Boolean(file.name)
      ? file.name.split('.')[0]
      : '';
    const extension = Boolean(file.name)
      ? file.name.split('.')[1]
      : '';

    return (
      <input
        type="text"
        defaultValue={cleanName}
        className={styles.fileNameInput}
        onBlur={(e) => {
          const editedFiles = files.map((item, i) => {
            if (index === i) {
              const newName = Boolean(e.target.value)
                ? [e.target.value, extension].join('.')
                : item.name;
              return new File([item], newName, {type: item.type});
            } else return item
          })
          setFiles(editedFiles)
        }}
      />
    )
  };

  return (
    <div className={styles.modalContainer}>
      <label>
        <input
          id={fileTypeID}
          type="file"
          ref={selectedFile}
          multiple={multiple}
          hidden
          {...rest}
        />
      </label>
      <h2 className={styles.modalHeader}>{'העלאת קבצים'}</h2>
      <div
        ref={drop}
        className={classnames(styles.dragDropContainer, {[styles.dragDropActive]: canDrop && isOver})}
      >
        <div className={styles.dragDropHeader}>
          <img src={fileIcon} alt="file" className={styles.dragDropIcon}/>
          <div>{'ניתן לגרור קבצים ישירות לכאן או להדביק אותם'}</div>
        </div>
      </div>
      <div className={styles.fileListContainer}>
        {files.filter(Boolean).length > 0 ? (
          <>
            {files.map((file, index) => (
              <div className={styles.fileItem} key={index}>
                <img
                  src={getFileTypeIcon(file.type)}
                  alt={'type'}
                  className={styles.typeIcon}
                />
                <EditableFileName file={file} index={index}/>
                <img
                  src={remove}
                  alt={'remove'}
                  className={styles.removeIcon}
                  onClick={() => {
                    onRemoveFile(file.name)
                  }}
                />
              </div>
            ))}
          </>
        ) : <EmptyView customContainerClassName={styles.empty}/>}
      </div>
      <div className={styles.buttonsContainer}>
        <ButtonCommon
          label={'המשך'}
          onClick={() => {
            handleChange();
            handleCloseModal();
          }}
          disabled={files.length === 0}
          customContainerStyles={styles.buttonSubmitContainer}
        />
        <ButtonFileUpload
          fieldId={fileTypeID}
          multiple={multiple}
          setFiles={setFiles}
          {...rest}
        />
        <ButtonCommon
          label={'סגירה'}
          onClick={handleCloseModal}
          customContainerStyles={styles.buttonCancelContainer}
          customLabelStyles={styles.buttonCancelLabel}
        />
      </div>
    </div>
  );
};

const ButtonFileUpload = ({
                            fieldId,
                            setFiles,
                            multiple = false,
                            ...rest
                          }) => {
  const selectedFile = useRef(null);
  const handleChange = () => {
    const selected = multiple
      ? Object.values(selectedFile.current.files)
      : [selectedFile.current.files[0]];

    setFiles(selected);
  };

  return (
    <label className={styles.buttonUploadContainer}>
      {'בחירת קובץ'}
      <img src={fileIcon} alt="file" className={styles.icon}/>
      <input
        id={fieldId}
        type="file"
        ref={selectedFile}
        onChange={handleChange}
        multiple={multiple}
        hidden
        {...rest}
      />
    </label>
  );
};

ItemPasteFile.propTypes = {
  label: PropTypes.string,
  multiple: PropTypes.bool,
};

export default ItemPasteFile;
